import { useTranslation } from 'react-i18next';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { formatAmountWithCurrencyCode } from '@/helpers/finance';

interface BillTotalsCardProps {
  totalAmount: string;
  amountPaid: string;
  balance: string;
  currencyCode: string;
  totalErrorMessage?: string;
}

/** Displays the current bill financial summary. */
export function BillTotalsCard({
  totalAmount,
  amountPaid,
  balance,
  currencyCode,
  totalErrorMessage,
}: BillTotalsCardProps) {
  const { t } = useTranslation('authenticated.bills');
  return (
    <Card className="h-fit bg-surface lg:sticky lg:top-0">
      <CardHeader>
        <CardTitle>{t('details.totals')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <TotalField
          label={t('details.fields.billTotal')}
          value={formatAmountWithCurrencyCode(totalAmount, currencyCode)}
        />
        <TotalField
          label={t('details.fields.paid')}
          value={formatAmountWithCurrencyCode(amountPaid, currencyCode)}
        />
        <TotalField
          label={t('details.fields.balance')}
          value={formatAmountWithCurrencyCode(balance, currencyCode)}
        />
        {totalErrorMessage && (
          <p className="pt-2 text-sm font-medium text-destructive">{t(totalErrorMessage)}</p>
        )}
      </CardContent>
    </Card>
  );
}

function TotalField({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-3 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium tabular-nums">{value}</span>
    </div>
  );
}
